/* -----------------UI--------------- */
import { Divider, Drawer, Text, TouchableRipple } from "react-native-paper";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
/* -----------------Helpers & Hooks--------------- */
import { type Href, Link, router, usePathname } from "expo-router";

import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import useBreakpoints from "@hooks/useBreakpoints";
/* -----------------Child components--------------- */
import { Box } from "@memorang/ui";
import { useDevice } from "@memorang/ui";
import { Image } from "expo-image";
import { type ImageSourcePropType, Linking, Platform } from "react-native";
import type { IconSource } from "react-native-paper/lib/typescript/components/Icon";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import MemorangIcons from "../MemorangIcons";
import NotifyAppLaunchContainer from "../NotifyAppLaunchContainer";
import type { IconType } from "../types/icon";
import StoresDownloadBadge, {
	type StoreDownloadBadgeProps,
} from "./StoresDownloadBadge";

type FooterProps = {
	storeDownloadBadge?: StoreDownloadBadgeProps;
	userAvatar?: string;
	userName?: string;
	isCollapsed?: boolean;
	showNotifyDialog: boolean;
	notifyDialogTitle: string;
	notifyDialogBody: string;
	notifyDialogCta: string;
	handleNotifyDialogCtaPress: () => void;
	showUnlockPremiumWidget?: boolean;
	onUnlockPremiumWidgetPress?: () => void;
};

type AppLogo = {
	default: {
		light: ImageSourcePropType;
		dark: ImageSourcePropType;
	};
	stacked: {
		light: ImageSourcePropType;
		dark: ImageSourcePropType;
	};
};

type SectionItem = {
	label: string;
	focusedIcon?: IconSource;
	unfocusedIcon?: IconSource;
	right?: IconType;
	route: Href;
	subtitle?: string;
	custom?: boolean;
	isCollapsed?: boolean;
};
export type Section = {
	title: string;
	items: SectionItem[];
	customHeader?: () => React.ReactNode;
} & {
	isCollapsed?: boolean;
};

const isActiveRoute = (route: Href, pathname: string) => {
	return pathname === route;
};

const LeftDrawerListItem = ({
	label,
	focusedIcon,
	unfocusedIcon,
	right,
	route,
	subtitle,
	isCollapsed,
}: SectionItem) => {
	const theme = useAppTheme();

	const _isProfile = route === "/profile";
	const isActive = isActiveRoute(route, usePathname());

	const renderIcon = (active: boolean) => {
		const targetIcon = active ? focusedIcon : unfocusedIcon;
		if (typeof targetIcon === "function") {
			return targetIcon;
		}
		return ({ size }: { color: string; size: number }) => (
			<Icon
				// biome-ignore lint/suspicious/noExplicitAny: targetIcon can be function or name string
				name={targetIcon as any}
				size={size}
				color={active ? theme.colors.primary : theme.colors.onSurfaceVariant}
			/>
		);
	};

	return isCollapsed ? (
		<Drawer.CollapsedItem
			label={label}
			active={isActive}
			focusedIcon={focusedIcon}
			unfocusedIcon={unfocusedIcon}
			onPress={() => router.push(route)}
			theme={{
				colors: { error: theme.colors.onPrimaryContainer },
			}}
		/>
	) : (
		<Drawer.Item
			//@ts-ignore
			label={
				<Box>
					<Text
						variant="bodyMedium"
						style={{
							color: isActive
								? theme.colors.primary
								: theme.colors.onSurfaceVariant,
							fontWeight: isActive ? "600" : "400",
						}}
					>
						{label}
					</Text>
					{subtitle && <Text variant="labelSmall">{subtitle}</Text>}
				</Box>
			}
			active={isActive}
			icon={renderIcon(isActive)}
			style={{
				backgroundColor: isActive
					? theme.colors.primaryContainer
					: "transparent",
				borderRadius: theme.roundness,
				marginHorizontal: 12,
			}}
			onPress={() => {
				router.push(route);
			}}
			right={() =>
				right && (
					<Icon
						name={right}
						onPress={() => router.push("/account/settings")}
						size={24}
						color={theme.colors.onSurfaceVariant}
					/>
				)
			}
		/>
	);
};

const Section = ({ title, items, customHeader, isCollapsed }: Section) => {
	const theme = useAppTheme();
	const titleProps = !isCollapsed && title ? { title } : {};
	return (
		<Drawer.Section {...titleProps} showDivider={false}>
			{customHeader?.()}
			{items.map(
				({
					label,
					focusedIcon,
					unfocusedIcon,
					right,
					route,
					subtitle,
					custom,
				}) => {
					return (
						<LeftDrawerListItem
							key={label}
							label={label}
							isCollapsed={isCollapsed}
							focusedIcon={
								custom
									? () => (
											<MemorangIcons
												name={focusedIcon}
												size={24}
												color={theme.colors.onSurfaceVariant}
											/>
										)
									: focusedIcon
							}
							unfocusedIcon={
								custom
									? () => (
											<MemorangIcons
												name={unfocusedIcon}
												size={24}
												color={theme.colors.onSurfaceVariant}
											/>
										)
									: unfocusedIcon
							}
							right={right}
							route={route}
							custom={custom}
							subtitle={subtitle}
						/>
					);
				},
			)}
		</Drawer.Section>
	);
};

const Footer = ({
	storeDownloadBadge,
	isCollapsed,
	showNotifyDialog,
	notifyDialogTitle,
	notifyDialogBody,
	notifyDialogCta,
	handleNotifyDialogCtaPress,
	showUnlockPremiumWidget,
	onUnlockPremiumWidgetPress,
}: FooterProps) => {
	const { isMobile } = useDevice();
	const isWeb = Platform.OS === "web";
	const theme = useAppTheme();
	const privacyUrl = useAppStore((store) => store.app.links.privacy);
	const tosUrl = useAppStore((store) => store.app.links.tos);

	return (
		<Box
			style={{
				paddingBottom: 24,
			}}
			gap={16}
		>
			{!isCollapsed && (
				<Divider
					style={{
						marginHorizontal: 16,
						backgroundColor: theme.colors.outlineVariant,
						height: 1,
					}}
				/>
			)}

			{/* {!isCollapsed && showUnlockPremiumWidget && (
				<Box style={{ paddingHorizontal: 16 }}>
					<TouchableRipple
						onPress={onUnlockPremiumWidgetPress}
						style={{
							backgroundColor: theme.colors.surfaceVariant,
							borderRadius: theme.roundness,
							padding: 16,
						}}
					>
						<Box
							flexDirection="row"
							alignItems="center"
							justifyContent="space-between"
							gap={8}
						>
							<Text
								style={{
									fontFamily: "OpenSans-SemiBold",
									fontSize: 14,
									fontWeight: "600",
									color: theme.colors.onSurfaceVariant,
									flexShrink: 1,
								}}
							>
								Unlock premium content
							</Text>
							<Icon
								name="chevron-right"
								size={24}
								color={theme.colors.onSurfaceVariant}
							/>
						</Box>
					</TouchableRipple>
				</Box>
			)} */}

			{!isMobile && isWeb && !isCollapsed && storeDownloadBadge && (
				<Box
					style={{
						paddingHorizontal: 16,
						width: "100%",
						flexDirection: "column",
						gap: 8,
					}}
				>
					{showNotifyDialog ? (
						<NotifyAppLaunchContainer
							title={notifyDialogTitle}
							body={notifyDialogBody}
							cta={notifyDialogCta}
							handleCtaPress={handleNotifyDialogCtaPress}
						>
							<StoresDownloadBadge {...storeDownloadBadge} />
						</NotifyAppLaunchContainer>
					) : (
						<StoresDownloadBadge {...storeDownloadBadge} />
					)}
				</Box>
			)}

			{!isCollapsed && (
				<Box
					flexDirection="row"
					justifyContent="center"
					alignItems="center"
					gap={4}
					style={{ paddingHorizontal: 16, marginTop: 4 }}
				>
					<TouchableRipple onPress={() => Linking.openURL(privacyUrl)}>
						<Text
							style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}
						>
							Privacy
						</Text>
					</TouchableRipple>
					<Box
						width={3}
						height={3}
						borderRadius={2}
						backgroundColor={theme.colors.onSurfaceVariant}
					/>
					<TouchableRipple onPress={() => Linking.openURL(tosUrl)}>
						<Text
							style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}
						>
							Terms
						</Text>
					</TouchableRipple>
					<Box
						width={3}
						height={3}
						borderRadius={2}
						backgroundColor={theme.colors.onSurfaceVariant}
					/>
					<TouchableRipple onPress={() => Linking.openURL(privacyUrl)}>
						<Text
							style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}
						>
							Policy
						</Text>
					</TouchableRipple>
				</Box>
			)}
		</Box>
	);
};

type Props = {
	sections: Section[];
	isCollapsed?: boolean;
	drawerHeader: {
		appLogo: AppLogo;
		title?: string;
		subtitle?: string;
		renderFab?: () => React.ReactNode;
	};
	footer: FooterProps;
};

const DrawerHeader = ({
	appLogo,
	title,
	subtitle,
	renderFab,
	isCollapsed,
}: {
	appLogo: AppLogo;
	title?: string;
	subtitle?: string;
	renderFab?: () => React.ReactNode;
	isCollapsed?: boolean;
}) => {
	const theme = useAppTheme();
	const { smUp, mdDown } = useBreakpoints();
	const showCollapsed = isCollapsed || (smUp && mdDown);
	const finalAppLogo = theme.dark
		? showCollapsed
			? appLogo.stacked.dark
			: appLogo.default.dark
		: showCollapsed
			? appLogo.stacked.light
			: appLogo.default.light;

	const insets = useSafeAreaInsets();

	return (
		<Box
			paddingHorizontal={showCollapsed ? 12 : 24}
			paddingBottom={16}
			paddingTop={insets.top}
		>
			<Link href={"/home" as never}>
				<Box
					style={{
						width: showCollapsed ? 55 : 200,
						height: showCollapsed ? 55 : 64,
					}}
				>
					<Image
						contentFit="contain"
						source={finalAppLogo}
						transition={400}
						style={{
							width: "100%",
							height: "100%",
							alignSelf: "flex-start",
						}}
					/>
				</Box>
			</Link>
			{(title || subtitle) && (
				<Box>
					<Text variant="titleMedium">{title}</Text>
					<Text variant="bodySmall">{subtitle}</Text>
				</Box>
			)}
			{renderFab?.()}
		</Box>
	);
};
export const DrawerContents = ({
	sections,
	drawerHeader: { appLogo, title, subtitle, renderFab },
	footer,
	isCollapsed,
}: Props) => {
	const theme = useAppTheme();

	return (
		<Box
			style={{
				flexGrow: 1,
				justifyContent: "space-between",
			}}
			backgroundColor={theme.colors.surface}
		>
			<Box>
				<DrawerHeader
					title={title}
					subtitle={subtitle}
					appLogo={appLogo}
					renderFab={renderFab}
					isCollapsed={isCollapsed}
				/>
				{sections.map((section) => (
					<Section
						key={section.title}
						title={section.title}
						items={section.items}
						customHeader={section.customHeader}
						isCollapsed={isCollapsed}
					/>
				))}
			</Box>
			<Footer {...footer} isCollapsed={isCollapsed} />
		</Box>
	);
};
